import { useEffect } from "react";
import { attachPositionGizmoController } from "../../selected-body-transform-gizmo/positionGizmoController";
import { attachRotationGizmoController } from "../../selected-body-transform-gizmo/rotationGizmoController";
import { attachScaleGizmoController } from "../../selected-body-transform-gizmo/scaleGizmoController";
import { hasPhysicsWorker } from "../../physics/workerHandle";
import { usePhysicsStore } from "../../stores/physicsStore";
import { useSelectionStore } from "../../stores/selectionStore";

export function useGizmoControllers({
  showPositionGizmo,
  showRotationGizmo,
  showScaleGizmo,
  sceneRef,
  cameraRef,
  rendererRef,
  controlsRef,
  sceneObjectsRef,
  jointObjectsRef,
  positionGizmoControllerRef,
  rotationGizmoControllerRef,
  scaleGizmoControllerRef,
  isOrbitingRef,
  resolveSelectedBodyUuidForGizmo,
  resolveSelectedTargetForPositionGizmo,
  resolveSelectedTargetForRotationGizmo,
  resolveSelectedTargetForScaleGizmo,
  resolveSelectedVisualMeshGeomTargetsForGizmo,
}) {
  // Custom position gizmo: attach when enabled in the inspector, detach otherwise.
  useEffect(() => {
    if (!showPositionGizmo) return undefined;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return undefined;
    const controller = attachPositionGizmoController({
      sceneRef,
      cameraRef,
      rendererRef,
      controlsRef,
      sceneObjectsRef,
      jointObjectsRef,
      getSelectedBodyUuid: resolveSelectedBodyUuidForGizmo,
      getSelectedTarget: resolveSelectedTargetForPositionGizmo,
      getSelectedGeomTargets: resolveSelectedVisualMeshGeomTargetsForGizmo,
      getSelectedBodies: () => useSelectionStore.getState().selectedBodies,
      isDisabled: () => {
        const state = usePhysicsStore.getState();
        return !!(state.isRunning && hasPhysicsWorker());
      },
      isOrbiting: () => !!isOrbitingRef.current,
    });
    positionGizmoControllerRef.current = controller;
    return () => {
      positionGizmoControllerRef.current = null;
      if (typeof controller?.detach === "function") controller.detach();
    };
  }, [
    showPositionGizmo,
    sceneRef,
    cameraRef,
    rendererRef,
    controlsRef,
    sceneObjectsRef,
    jointObjectsRef,
    positionGizmoControllerRef,
    isOrbitingRef,
    resolveSelectedBodyUuidForGizmo,
    resolveSelectedTargetForPositionGizmo,
    resolveSelectedVisualMeshGeomTargetsForGizmo,
  ]);

  // Custom rotation gizmo: attach when enabled in the inspector, detach otherwise.
  useEffect(() => {
    if (!showRotationGizmo) return undefined;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return undefined;
    const controller = attachRotationGizmoController({
      sceneRef,
      cameraRef,
      rendererRef,
      controlsRef,
      sceneObjectsRef,
      getSelectedBodyUuid: resolveSelectedBodyUuidForGizmo,
      getSelectedTarget: resolveSelectedTargetForRotationGizmo,
      getSelectedGeomTargets: resolveSelectedVisualMeshGeomTargetsForGizmo,
      getSelectedBodies: () => useSelectionStore.getState().selectedBodies,
      isDisabled: () => {
        const state = usePhysicsStore.getState();
        return !!(state.isRunning && hasPhysicsWorker());
      },
      isOrbiting: () => !!isOrbitingRef.current,
    });
    rotationGizmoControllerRef.current = controller;
    return () => {
      rotationGizmoControllerRef.current = null;
      if (typeof controller?.detach === "function") controller.detach();
    };
  }, [
    showRotationGizmo,
    sceneRef,
    cameraRef,
    rendererRef,
    controlsRef,
    sceneObjectsRef,
    rotationGizmoControllerRef,
    isOrbitingRef,
    resolveSelectedBodyUuidForGizmo,
    resolveSelectedTargetForRotationGizmo,
    resolveSelectedVisualMeshGeomTargetsForGizmo,
  ]);

  // Custom scale gizmo: attach only for selected visual geometry or visual mesh assets.
  useEffect(() => {
    if (!showScaleGizmo) return undefined;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return undefined;
    const controller = attachScaleGizmoController({
      sceneRef,
      cameraRef,
      rendererRef,
      controlsRef,
      getSelectedTarget: resolveSelectedTargetForScaleGizmo,
      isDisabled: () => {
        const state = usePhysicsStore.getState();
        return !!(state.isRunning && hasPhysicsWorker());
      },
      isOrbiting: () => !!isOrbitingRef.current,
    });
    scaleGizmoControllerRef.current = controller;
    return () => {
      scaleGizmoControllerRef.current = null;
      if (typeof controller?.detach === "function") controller.detach();
    };
  }, [
    showScaleGizmo,
    sceneRef,
    cameraRef,
    rendererRef,
    controlsRef,
    scaleGizmoControllerRef,
    isOrbitingRef,
    resolveSelectedTargetForScaleGizmo,
  ]);
}
